import { TimelineMode } from '@src/editor';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { runInAction } from 'mobx';
import { v4 as uuid } from 'uuid';

import { DEFAULT_ANIMATION_SPEED } from '../config/imageSettings';
import { LogoElement } from '../editor/models';
import { MediaPositioner } from '../editor/models/aspectRatio/MediaPositioner';
import { Timeline } from '../editor/models/Timeline';
import { useStore } from '../stores-v2/StoreContext';
import { SidebarOption } from '../types.ts/general';
import {
  AspectRatio,
  VideoDimensionsHeight,
  VideoDimensionsWidth,
} from '../types.ts/video';
import applyImageOptimizations from '../utility/applyImageOptimizations';
import { getImageWithBlackFrameElements } from '../utility/elements';
import { getRandomFileName } from '../utility/general';
import {
  calculateLogoPosition,
  combineVideoDataFromHeadlessTracks,
} from '../utility/logoPlacement';
import {
  getImageNaturalDimensions,
  getVideoNaturalDimensions,
} from '../utility/naturalDimensions';

import type { AddVideoElementOptions } from '../editor/commands/AddVideoElementCommand';
import type { PhotoArtifactTab } from '../types.ts/story';
import type { Element } from '@src/editor/models/Element';
import type {
  NaturalDimensions,
  VideoDimensions,
} from '@src/editor/types/aspectRatio';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

const logger = LogManager.getLogger('hook:useAddMediaToTimeline');

/**
 * Interface representing a media file item that can be added to the timeline
 */
export type MediaFileItem = {
  id: string;
  src: string;
  title?: string;
  description: string;
  thumbnailUrl?: string;
  duration?: number; // For videos, duration in seconds
  aspectRatio?: number; // Width/height ratio for layout calculations
  alt?: string; // Alt text for accessibility
  slug?: string; // Filename/slug for the asset
  type?: 'artifact' | 'stock' | 'ai'; // Source type for tracking
  attribution?: any; // Attribution data for stock photos
};

export type MediaFilePlaceholder = Omit<MediaFileItem, 'src'>;

/**
 * Interface for AI photo upload data
 */
export interface AIPhotoData {
  type: 'ai';
  url: string;
  fileName: string;
  alt: string;
  title: string;
}

/**
 * Media type for determining which command to use
 */
export type MediaType = 'video' | 'photo' | 'logo' | 'audio';

/**
 * Logo-specific options for timeline addition
 */
export interface LogoToTimelineOptions {
  /**
   * Source aspect ratio for positioning calculations
   */
  sourceAspectRatio: number;

  /**
   * Video aspect ratio for positioning calculations
   */
  videoAspectRatio: AspectRatio;

  /**
   * Logo opacity (default: '40%')
   */
  opacity?: string;

  /**
   * Logo fit mode (default: 'contain')
   */
  fit?: 'cover' | 'contain' | 'fill';

  track?: number;

  /**
   * When true, stamp the logo element `locked: true` (non-interactive) for the
   * video-template "Lock Position" feature.
   */
  locked?: boolean;

  logoPosition?: {
    x?: string;
    y?: string;
    width?: string;
    height?: string;
    x_anchor?: string;
    y_anchor?: string;
  };
}

/**
 * Options for adding media to timeline
 */
export interface AddMediaToTimelineOptions {
  /**
   * Whether to fit the media duration to the timeline end
   */
  fitToEnd?: boolean;

  /**
   * Default volume for video elements (0-100)
   */
  volume?: number;

  /**
   * How the media should be resized to fit the element ('cover', 'contain', 'fill')
   */
  fit?: 'cover' | 'contain' | 'fill';

  /**
   * Whether video should loop when it reaches the end
   */
  loop?: boolean;

  /**
   * Whether to automatically add fade in/out effects
   */
  autoFade?: boolean;

  /**
   * Custom duration for photos (in seconds)
   */
  photoDuration?: number;

  /**
   * Whether to apply smart crop for images
   */
  smartCrop?: boolean;

  /**
   * Tab context for determining media behavior (logo vs regular photo)
   */
  tab?: PhotoArtifactTab;

  /**
   * For AI photos: search value used for title/alt text
   */
  searchValue?: string;

  /**
   * Opacity for logo elements (as percentage string like '40%')
   */
  opacity?: string;

  /**
   * Custom positioning and sizing for logos
   */
  logoPosition?: {
    x?: string;
    y?: string;
    width?: string;
    height?: string;
    x_anchor?: string;
    y_anchor?: string;
  };

  /**
   * When true, stamp the created element `locked: true` so it is non-interactive
   * on the canvas (Creatomate's native flag). Used by the video-template
   * "Lock Position" feature for the logo element.
   */
  locked?: boolean;

  /**
   * Whether to create composition elements for portrait images
   */
  useComposition?: boolean;

  /**
   * Custom animations to apply to the element
   */
  animations?: any[];

  /**
   * Custom track to place element on
   */
  track?: number;

  /**
   * Custom time position to place element at
   */
  time?: number;

  /**
   * Custom element duration
   */
  duration?: number;

  /**
   * Pre-defined id.
   * Needed for cases like transcript highlighting where punchlist id needs to
   * be attached to transcript and timeline element
   */
  id?: string;
}

const isMediaFileItem = (
  item: MediaFileItem | MediaFilePlaceholder,
): item is MediaFileItem => {
  return 'src' in item && typeof item.src === 'string';
};

/**
 * Hook for adding video/photo elements to the headless editor timeline
 * Handles both headless editor commands and fallback to original implementation
 *
 * @param editorStore - The editor store to use for adding elements
 * @param videoCreatorOverride - Optional videoCreator to use instead of the global one.
 *   This is needed for clip mode where we need to store metadata in the clip's extraElementData
 *   instead of the global store.
 */
export const useAddMediaToTimeline = ({
  videoCreator,
  editorStore,
}: {
  videoCreator: IVideoCreatorStore;
  editorStore: EditorStoreLike;
}) => {
  const { datoClientStore } = useStore();

  /**
   * Simple timeout-based auto-selection for newly added elements
   * Uses 300ms delay to ensure element is in state before selecting
   */
  const autoSelectElementAfterDelay = (elementId: string) => {
    setTimeout(async () => {
      if (elementId) {
        console.log(`Auto-selecting newly added element: ${elementId}`);
        try {
          await videoCreator.setActiveElements(elementId);
        } catch (error) {
          // Nothing catches a throw from this timer callback, and the routing
          // below must still run or the panel is left on the wrong tab.
          logger.warn(
            'Failed to select newly added element',
            extractErrorInfo(error),
            { elementId },
          );
        }
        // Routed here, with the selection, rather than by the callers before
        // this timeout: `editing` with nothing selected is the right panel's
        // "nothing to edit" state, and its guard collapses the sidebar — so for
        // 300ms after every add the panel the media was added FROM closed
        // itself, and the routing that follows the selection could no longer
        // reopen it (collapsing also clears `isSidebarExpanded`).
        videoCreator.sidebarOptions = SidebarOption.editing;
      } else {
        logger.warn(
          `Could not find newly added element with ID: ${elementId}`,
          {
            elementId,
          },
        );
      }
    }, 300);
  };

  /**
   * Adds a video or photo element to the timeline
   * @param item The media file item to add
   * @param mediaType The type of media ('video', 'photo', or 'logo')
   * @param options Additional options for media handling
   * @returns Promise<boolean> indicating success
   */
  const addMediaToTimeline = async (
    item: MediaFileItem | MediaFilePlaceholder,
    mediaType: MediaType,
    options: AddMediaToTimelineOptions | LogoToTimelineOptions = {},
    shouldSetActiveElement: boolean = true,
    createUndoEntry: boolean = true,
  ): Promise<{ success: boolean; element: Element | null }> => {
    try {
      return await addMediaViaHeadlessEditor(
        item,
        mediaType,
        options,
        shouldSetActiveElement,
        createUndoEntry,
      );
    } catch (error) {
      console.error(`Failed to add ${mediaType} to timeline:`, error);
      videoCreator.showToast({
        state: 'error',
        message: `Failed to add ${mediaType}. Please try again.`,
      });
      return { success: false, element: null };
    }
  };

  /**
   * Adds media using the headless editor commands
   */
  const addMediaViaHeadlessEditor = async (
    item: MediaFileItem | MediaFilePlaceholder,
    mediaType: MediaType,
    options: AddMediaToTimelineOptions | LogoToTimelineOptions,
    shouldSetActiveElement: boolean = true,
    createUndoEntry: boolean = true,
  ): Promise<{ success: boolean; element: Element | null }> => {
    if (mediaType === 'video' && isMediaFileItem(item)) {
      return await addVideoViaHeadlessEditor(
        item,
        options as AddMediaToTimelineOptions,
        shouldSetActiveElement,
        createUndoEntry,
      );
    } else if (mediaType === 'photo') {
      return await addPhotoViaHeadlessEditor(
        item,
        options as AddMediaToTimelineOptions,
        shouldSetActiveElement,
        createUndoEntry,
      );
    } else if (mediaType === 'logo' && isMediaFileItem(item)) {
      return addLogoViaHeadlessEditor(
        item,
        options as LogoToTimelineOptions,
        shouldSetActiveElement,
        createUndoEntry,
      );
    } else if (mediaType === 'audio' && isMediaFileItem(item)) {
      return addAudioViaHeadlessEditor(
        item,
        options as AddMediaToTimelineOptions,
        shouldSetActiveElement,
        createUndoEntry,
      );
    }

    throw new Error(`Unsupported media type: ${mediaType}`);
  };

  /**
   * Adds a video using AddVideoElementCommand
   */
  const addVideoViaHeadlessEditor = async (
    item: MediaFileItem,
    options: AddMediaToTimelineOptions,
    shouldSetActiveElement: boolean = true,
    createUndoEntry: boolean = true,
  ): Promise<{ success: boolean; element: Element | null }> => {
    let videoDimensions = {
      x: '50%',
      y: '50%',
      width: '100%',
      height: '100%',
    };

    // Apply aspect ratio adjustments
    if (
      !videoCreator.hasLandscapeAspectRatio() &&
      (options.fit || 'cover') === 'cover'
    ) {
      if (!videoCreator.currentVideo) {
        throw new Error(
          'Cannot apply aspect ratio adjustments on add video: currentVideo not set',
        );
      }
      const currentVideoDimensions = {
        width: videoCreator.currentVideo.videoSource.width,
        height: videoCreator.currentVideo.videoSource.height,
      };
      videoDimensions = await calculateMediaAspectRatioAdjustments(
        item.src,
        currentVideoDimensions,
        options.fit || 'cover',
        'video',
      );
    }

    // Create video element for headless editor
    const elementProps = {
      name: item.title || item.description || 'Video Element',
      type: 'video',
      source: item.src,
      autoplay: true,
      time: videoCreator.time || 0, // Use current playhead position
      duration: item.duration || 8, // Default to 8 seconds if duration not provided
      fit: options.fit || 'cover',
      loop: options.loop || false,
      // Apply calculated dimensions
      ...videoDimensions,
    };

    // TODO: mapItemToElementProps: (item: MediaFileItem) => {}

    const videoElement = Timeline.createElement('video', elementProps);

    // Prepare video-specific options for the command
    const commandOptions: AddVideoElementOptions = {
      volume: options.volume || 100,
      fit: options.fit || 'cover',
      loop: options.loop || false,
      autoFade: options.autoFade || false,
    };

    // Use AddVideoElementCommand
    const command = editorStore.editor.commandAddVideoElement(
      videoElement,
      undefined, // Let the command auto-select track
      TimelineMode.DEFAULT, // Use default timeline mode, ripple mode is not enabled yet
      commandOptions,
      createUndoEntry,
    );

    if (command) {
      console.log(
        `Video added via headless editor: ${
          elementProps.name
        } to track ${command.getFinalTrackId()}`,
      );
      if (shouldSetActiveElement) {
        autoSelectElementAfterDelay(videoElement.id);
      }

      return { success: true, element: videoElement };
    } else {
      throw new Error('Failed to execute AddVideoElementCommand');
    }
  };

  const addAudioViaHeadlessEditor = (
    item: MediaFileItem,
    options: AddMediaToTimelineOptions,
    shouldSetActiveElement: boolean = true,
    createUndoEntry: boolean = true,
  ): { success: boolean; element: Element | null } => {
    // Create audio element for headless editor
    const elementProps = {
      name: item.title,
      type: 'audio',
      source: item.src,
      autoplay: true,
      time: options.time ?? videoCreator.time ?? 0,
      duration: item.duration,
      volume: 10,
    };

    const audioElement = Timeline.createElement('audio', elementProps);

    // Use AddMusicElementCommand with music-specific options
    const command = editorStore.editor.commandAddMusicElement(
      audioElement,
      undefined, // Let the command auto-select track
      TimelineMode.DEFAULT, // Use default timeline mode, ripple mode is not enabled yet
      {
        fitToEnd: options?.fitToEnd || false,
        volume: 10,
        autoFade: false, // Could be made configurable
      },
      createUndoEntry,
    );

    if (command) {
      console.log(
        `Music added via headless editor: ${
          item.title
        } to track ${command.getFinalTrackId()}`,
      );
      if (shouldSetActiveElement) {
        autoSelectElementAfterDelay(audioElement.id);
      }
      return { success: true, element: audioElement };
    } else {
      throw new Error('Failed to execute AddMusicElementCommand');
    }
  };

  /**
   * Adds a photo using AddImageElementCommand (when available) or fallback to AddMediaElementCommand
   */
  const addPhotoViaHeadlessEditor = async (
    item: MediaFileItem | MediaFilePlaceholder,
    options: AddMediaToTimelineOptions,
    shouldSetActiveElement: boolean = true,
    createUndoEntry: boolean = true,
  ): Promise<{ success: boolean; element: Element | null }> => {
    // Handle AI photo upload if needed
    let processedItem = item;
    if (item.type === 'ai' && isMediaFileItem(item)) {
      const uploadedData = await uploadAiPhoto(
        item.src,
        options.searchValue || '',
      );
      if (!uploadedData) {
        throw new Error('Failed to upload AI photo');
      }
      processedItem = {
        ...item,
        src: uploadedData.url || item.src,
        id: uploadedData.id || item.id,
        title: options.searchValue,
        description: options.searchValue || '',
        alt: options.searchValue,
      };
    }

    let optimizedSrc;
    // Apply image optimizations
    if (isMediaFileItem(processedItem)) {
      optimizedSrc = applyImageOptimizations(processedItem.src);
    }

    // Default to cover fit instead of black frame composition
    // Only use composition if explicitly requested via options.useComposition = true
    const useComposition = options.useComposition === true;

    // Set up default animations if not provided
    const defaultAnimations = [
      {
        start_scale: '100%',
        end_scale: DEFAULT_ANIMATION_SPEED,
        x_anchor: '50%',
        y_anchor: '50%',
        fade: false,
        scope: 'element',
        easing: 'linear',
        type: 'scale',
        arbor_subType: 'zoomIn',
      },
    ];

    // Calculate track assignment using getFreeMediaTrack for consistency with original implementation
    const photoDuration = options.duration || options.photoDuration || 4;
    const freeTrack =
      options.track ||
      videoCreator.getFreeMediaTrack('image', photoDuration, options.time);

    let imageDimensions = {
      x: '50%',
      y: '50%',
      width: '100%',
      height: '100%',
    };

    // Apply aspect ratio adjustments
    if (
      !useComposition &&
      !videoCreator.hasLandscapeAspectRatio() &&
      (options.fit || 'cover') === 'cover' &&
      optimizedSrc
    ) {
      if (!videoCreator.currentVideo) {
        throw new Error(
          'Cannot apply aspect ratio adjustments on add photo: currentVideo not set',
        );
      }
      const currentVideoDimensions = {
        width: videoCreator.currentVideo.videoSource.width,
        height: videoCreator.currentVideo.videoSource.height,
      };
      imageDimensions = await calculateMediaAspectRatioAdjustments(
        optimizedSrc,
        currentVideoDimensions,
        options.fit || 'cover',
        'image',
      );
    }

    const elementProps =
      useComposition && optimizedSrc
        ? {
            type: 'composition',
            duration: photoDuration,
            time: options.time || videoCreator.time || 0,
            track: freeTrack,
            source: null,
            elements: getImageWithBlackFrameElements({
              source: optimizedSrc,
            }).map(elementData =>
              Timeline.createElement(elementData.type as any, elementData),
            ),
            animations: options.animations || defaultAnimations,
            id: options.id,
          }
        : {
            type: 'image',
            source: optimizedSrc,
            time: options.time || videoCreator.time || 0,
            duration: photoDuration,
            fit: options.fit || 'cover',
            smart_crop: options.smartCrop !== false,
            autoplay: true,
            animations: options.animations || defaultAnimations,
            track: freeTrack,
            // Apply calculated dimensions
            ...imageDimensions,
            visible: true,
            locked: false,
            dynamic: false,
            id: options.id,
          };

    const imageElement = Timeline.createElement(
      elementProps.type as any,
      elementProps,
    );

    // Use the base AddMediaElementCommand for now
    const command = editorStore.editor.commandAddMediaElement(
      imageElement,
      options.track ? options.track.toString() : undefined, // Convert track number to string
      TimelineMode.DEFAULT, // Use default timeline mode, ripple mode is not enabled yet
      createUndoEntry,
    );

    if (command) {
      console.log(
        `Photo added via headless editor: ${
          processedItem.title || processedItem.description || 'Photo Element'
        } to track ${command.getFinalTrackId()}`,
      );

      // Store photo data for Dato if it's stock or AI
      if (
        optimizedSrc &&
        (processedItem.type === 'stock' || processedItem.type === 'ai')
      ) {
        await storePhotoDataForDato({ ...processedItem, src: optimizedSrc });
      }

      if (shouldSetActiveElement) {
        autoSelectElementAfterDelay(imageElement.id);
      }

      return { success: true, element: imageElement };
    } else {
      throw new Error('Failed to execute AddMediaElementCommand for image');
    }
  };

  /**
   * Adds a logo using headless editor commands
   */
  const addLogoViaHeadlessEditor = (
    item: MediaFileItem, // PhotoAsset
    options: LogoToTimelineOptions,
    shouldSetActiveElement: boolean = true,
    createUndoEntry: boolean = true,
  ): { success: boolean; element: Element | null } => {
    const optimizedSrc = applyImageOptimizations(item.src);
    const sourceAspectRatio = item.aspectRatio || 1;
    const logoId = uuid();

    if (!videoCreator.currentVideo) {
      throw new Error('Cannot add logo via headless: currentVideo not set');
    }

    // Store logo metadata
    videoCreator.currentVideo.extraElementData = {
      ...(videoCreator.currentVideo?.extraElementData || {}),
      [`logo_el_${logoId}`]: {
        isLogo: true,
      },
    };

    // Get transcript track for filtering
    const transcriptTrack = editorStore.editor.getTranscriptTrack();

    let time = 0;
    let duration = null;

    // Get video timing data from editor.timeline using headless-specific function
    const tracks = editorStore.editor.timeline.tracks;
    const videoDataPerTrack = combineVideoDataFromHeadlessTracks(
      tracks,
      transcriptTrack?.id,
    );

    if (videoDataPerTrack.length) {
      videoDataPerTrack.sort((a, b) => (b.duration || 0) - (a.duration || 0));
      ({ time, duration } = videoDataPerTrack[0]);
    }

    let track = options?.track;
    if (!track) {
      track = videoCreator.getMaxTrack() + 1;
    }

    // Calculate logo positioning based on aspect ratio
    const logoPosition = calculateLogoPosition(
      sourceAspectRatio,
      videoCreator.currentVideo?.aspectRatio,
    );

    const elementProps = {
      id: logoId,
      type: 'image',
      source: optimizedSrc,
      duration,
      time,
      track,
      opacity: options.opacity ?? '100%',
      fit: options.fit ?? 'contain',
      x: options.logoPosition?.x ?? logoPosition.x,
      y: options.logoPosition?.y ?? logoPosition.y,
      width: options.logoPosition?.width ?? logoPosition.width,
      height: options.logoPosition?.height ?? logoPosition.height,
      x_anchor: options.logoPosition?.x_anchor ?? logoPosition.x_anchor,
      y_anchor: options.logoPosition?.y_anchor ?? logoPosition.y_anchor,
      visible: true,
      locked: options.locked ?? false,
      dynamic: false,
    };

    const logoElement = new LogoElement(elementProps);
    logoElement.coupledTrackId = transcriptTrack?.id ?? null;
    const command = editorStore.editor.commandAddMediaElement(
      logoElement,
      track ? track.toString() : undefined, // Convert track number to string
      TimelineMode.OVERLAP, // Use default timeline mode, ripple mode is not enabled yet
      createUndoEntry,
    );

    if (command) {
      console.log(
        `Logo added via headless editor: ${
          item.title || item.description || 'Logo Element'
        } to track ${command.getFinalTrackId()}`,
      );

      if (shouldSetActiveElement) {
        autoSelectElementAfterDelay(logoElement.id);
      }
      return { success: true, element: logoElement };
    } else {
      throw new Error('Failed to execute AddMediaElementCommand for logo');
    }
  };

  /**
   * Uploads AI photo and returns upload data
   */
  const uploadAiPhoto = async (url: string, searchValue: string) => {
    try {
      if (!datoClientStore) return null;

      const fileName = getRandomFileName(searchValue);

      const newPhotoData: AIPhotoData = {
        type: 'ai',
        url,
        fileName,
        alt: searchValue,
        title: searchValue,
      };

      const newUpload =
        await datoClientStore.assetRepository?.uploadFile(newPhotoData);
      return { url: newUpload?.url, id: newUpload?.id, fileName };
    } catch (error) {
      console.error('Error uploading AI photo:', error);
      return null;
    }
  };

  /**
   * Store photo data for Dato CMS
   */
  const storePhotoDataForDato = async (item: MediaFileItem) => {
    if (item.type === 'stock' || item.type === 'ai') {
      runInAction(() => {
        videoCreator.photoDataForDato = {
          ...videoCreator.photoDataForDato,
          [item.id]: {
            type: item.type!,
            url: item.src,
            fileName: item.slug || item.title || 'untitled',
            title: item.title || item.description,
            alt: item.alt || item.description,
          },
        };
      });
    }
  };

  return {
    addMediaToTimeline,
    addLogoViaHeadlessEditor,
  };
};

async function calculateMediaAspectRatioAdjustments(
  mediaSrc: string,
  currentVideoDimensions: { width: number; height: number },
  fit: string = 'cover',
  mediaType: 'image' | 'video' = 'image',
): Promise<{ x: string; y: string; width: string; height: string }> {
  if (fit !== 'cover') return MediaPositioner.getDefaultPositioning();

  const prevDimensions: VideoDimensions = {
    width: VideoDimensionsWidth.AR_16_9,
    height: VideoDimensionsHeight.AR_16_9,
    aspectRatio: AspectRatio.AR_16_9,
  };

  const newDimensions: VideoDimensions = {
    width: currentVideoDimensions.width,
    height: currentVideoDimensions.height,
    aspectRatio:
      currentVideoDimensions.width > currentVideoDimensions.height
        ? AspectRatio.AR_16_9
        : currentVideoDimensions.width === currentVideoDimensions.height
          ? AspectRatio.AR_1_1
          : AspectRatio.AR_9_16,
  };

  const mediaPositioner = new MediaPositioner(prevDimensions, newDimensions);

  let naturalDimensions: NaturalDimensions | undefined;
  try {
    if (mediaType === 'image') {
      const dimensions = await getImageNaturalDimensions(mediaSrc);
      naturalDimensions = dimensions || undefined;
    } else if (mediaType === 'video') {
      const dimensions = await getVideoNaturalDimensions(mediaSrc);
      naturalDimensions = dimensions || undefined;
    }
  } catch (error) {
    console.warn(`Failed to get natural dimensions for ${mediaType}:`, error);
  }

  return mediaPositioner.getAdjustedDimensions(naturalDimensions);
}

export default useAddMediaToTimeline;
